// ABOUTME: Panel component for full-report AI editing with inline word-level diffs.
// ABOUTME: Per-section accept/reject with individual apply buttons (hover-reveal).
// ABOUTME: Tone-only edits supported (no prompt required). Tone selector collapsed by default.
// ABOUTME: Applied sections disappear from diff list. Panel stays open after apply.
// ABOUTME: No fixed positioning — parent (ReportEditor) handles flex layout.

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  X,
  Check,
  RotateCcw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Zap,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { OleumIcon } from '@/components/icons/OleumIcon';
import { messageApi } from '@/services/api/client';
import type { FullReportEditSuggestion, FullEditSectionSuggestion, ReportSection } from '@/types/api';

interface FullAIEditPanelProps {
  isLoading: boolean;
  suggestion: FullReportEditSuggestion | null;
  error: string | null;
  onClose: () => void;
  onSubmit: (prompt: string, tone: string) => Promise<void>;
  onApplySelected: (acceptedSectionIds: Set<string>, applyReorder?: boolean) => void;
  onApplySingle: (sectionId: string) => Promise<void>;
  onRejectAll: () => void;
  /** Signal object: when a new object is set, the panel restores the section with this ID to the diff list */
  undoSignal?: { sectionId: string } | null;
  /** Optional initial prompt to pre-fill (e.g., from text selection) */
  initialPrompt?: string;
}

const TONE_OPTIONS = [
  { value: '', label: 'Keep current' },
  { value: 'professional', label: 'Professional' },
  { value: 'executive', label: 'Executive' },
  { value: 'technical', label: 'Technical' },
  { value: 'casual', label: 'Casual' },
  { value: 'journalist', label: 'Journalist' },
] as const;

export type ToneValue = Exclude<(typeof TONE_OPTIONS)[number]['value'], ''>;

// --- Word-level diff utilities ---

type DiffType = 'equal' | 'add' | 'remove';
interface DiffSegment { type: DiffType; text: string }

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

function computeWordDiff(oldHtml: string, newHtml: string): DiffSegment[] {
  const oldText = stripHtml(oldHtml);
  const newText = stripHtml(newHtml);

  if (oldText === newText) return [{ type: 'equal', text: oldText }];
  if (!oldText) return [{ type: 'add', text: newText }];
  if (!newText) return [{ type: 'remove', text: oldText }];

  const oldWords = oldText.split(/\s+/).filter(Boolean);
  const newWords = newText.split(/\s+/).filter(Boolean);
  const m = oldWords.length;
  const n = newWords.length;

  // Fall back to simple add/remove for very large sections to avoid UI jank
  if (m > 300 || n > 300) {
    return [{ type: 'remove', text: oldText }, { type: 'add', text: newText }];
  }

  // LCS dynamic programming
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldWords[i - 1] === newWords[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack to build raw diff
  const raw: { type: DiffType; word: string }[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      raw.unshift({ type: 'equal', word: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.unshift({ type: 'add', word: newWords[j - 1] });
      j--;
    } else {
      raw.unshift({ type: 'remove', word: oldWords[i - 1] });
      i--;
    }
  }

  // Merge consecutive same-type segments
  const segments: DiffSegment[] = [];
  for (const item of raw) {
    const last = segments[segments.length - 1];
    if (last && last.type === item.type) {
      last.text += ' ' + item.word;
    } else {
      segments.push({ type: item.type, text: item.word });
    }
  }
  return segments;
}

/** Extract a readable label from section content (strip HTML, take first line) */
function extractSectionLabel(section: { type: string; content?: string }): string {
  // Friendly type names for non-text sections
  const typeLabels: Record<string, string> = {
    insight_reference: '📊 Embedded Insight',
    visualization: '📈 Chart',
    table: '📋 Table',
  };

  const text = section.content?.replace(/<[^>]*>/g, '').trim();

  if (section.type === 'insight_reference') {
    const prefix = typeLabels[section.type];
    return text ? `${prefix}: ${text.split('\n')[0].slice(0, 50)}` : prefix;
  }

  if (!text) return typeLabels[section.type] || section.type;
  const firstLine = text.split('\n')[0].slice(0, 60);
  return firstLine.length < text.split('\n')[0].length ? firstLine + '...' : firstLine;
}

// --- Typewriter placeholder hook ---

const TYPEWRITER_EXAMPLES = [
  'Make the tone more technical...',
  'Swap the executive summary and methodology sections',
  'Shorten to 2 pages, keep only key findings',
  'Add a recommendations section at the end',
  'Rewrite in a casual, conversational style',
  'Remove all jargon and simplify the language',
];

function pickRandomIndex(excludeIdx: number) {
  let idx: number;
  do {
    idx = Math.floor(Math.random() * TYPEWRITER_EXAMPLES.length);
  } while (idx === excludeIdx && TYPEWRITER_EXAMPLES.length > 1);
  return idx;
}

function useTypewriterPlaceholder(isActive: boolean) {
  const [text, setText] = useState('');
  const stateRef = useRef({ exampleIdx: pickRandomIndex(-1), charIdx: 0, phase: 'typing' as 'typing' | 'pausing' | 'deleting' | 'gap' });

  useEffect(() => {
    if (!isActive) {
      setText('');
      // Pick a new random example for next activation
      stateRef.current = { exampleIdx: pickRandomIndex(stateRef.current.exampleIdx), charIdx: 0, phase: 'typing' };
      return;
    }

    const s = stateRef.current;

    const tick = () => {
      const example = TYPEWRITER_EXAMPLES[s.exampleIdx];

      if (s.phase === 'typing') {
        s.charIdx++;
        setText(example.slice(0, s.charIdx));
        if (s.charIdx >= example.length) {
          s.phase = 'pausing';
          timer = setTimeout(tick, 1500);
          return;
        }
        timer = setTimeout(tick, 50);
      } else if (s.phase === 'pausing') {
        s.phase = 'deleting';
        timer = setTimeout(tick, 0);
      } else if (s.phase === 'deleting') {
        s.charIdx--;
        setText(example.slice(0, s.charIdx));
        if (s.charIdx <= 0) {
          s.phase = 'gap';
          timer = setTimeout(tick, 400);
          return;
        }
        timer = setTimeout(tick, 30);
      } else if (s.phase === 'gap') {
        s.exampleIdx = pickRandomIndex(s.exampleIdx);
        s.charIdx = 0;
        s.phase = 'typing';
        timer = setTimeout(tick, 0);
      }
    };

    let timer = setTimeout(tick, 500);
    return () => clearTimeout(timer);
  }, [isActive]);

  return text;
}

// --- Section diff component with accept/reject and inline diff ---

function SectionDiff({
  section,
  isAccepted,
  onToggleAccept,
  onApplySingle,
  isApplying,
  resolvedLabel,
}: {
  section: FullEditSectionSuggestion;
  isAccepted: boolean;
  onToggleAccept: () => void;
  onApplySingle: () => void;
  isApplying: boolean;
  resolvedLabel?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!section.changed) return null;

  const label = resolvedLabel || extractSectionLabel(section.original);
  const isDeleted = section.deleted;
  const isAdded = section.added;

  const diffSegments = useMemo(() => {
    if (isDeleted || isAdded) return [];
    return computeWordDiff(
      section.original.content || '',
      section.suggested.content || '',
    );
  }, [section, isDeleted, isAdded]);

  const effectiveLabel = isAdded
    ? extractSectionLabel(section.suggested) || 'New section'
    : label;

  return (
    <div
      className={`group/section border rounded-sm overflow-hidden mb-2 transition-colors ${
        isDeleted
          ? isAccepted
            ? 'border-red-300'
            : 'border-surface-200'
          : isAccepted
            ? 'border-emerald-300'
            : 'border-surface-200'
      }`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 ${
          isDeleted ? 'bg-red-50/60' : 'bg-surface-50'
        }`}
      >
        {/* Accept/reject checkbox */}
        <button
          type="button"
          onClick={onToggleAccept}
          className={`w-5 h-5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
            isAccepted
              ? 'bg-primary-600 border-primary-600 text-white'
              : 'border-surface-300 hover:border-surface-400'
          }`}
        >
          {isAccepted && <Check className="w-3 h-3" />}
        </button>

        {/* Expand/collapse (content changes and added sections — not deleted) */}
        {!isDeleted ? (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0 cursor-pointer p-0.5 hover:bg-surface-100 rounded-sm"
          >
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-surface-400" />
            )}
          </button>
        ) : (
          <span className="w-4.5 flex-shrink-0" />
        )}

        {/* Section label */}
        <span
          className={`text-xs truncate flex-1 text-left ${
            isDeleted ? 'text-red-700 line-through' : isAdded ? 'text-emerald-700' : 'text-surface-700'
          }`}
        >
          {effectiveLabel}
        </span>

        {/* Status badge */}
        {isDeleted ? (
          <span className="text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded-sm font-medium flex-shrink-0">
            removed
          </span>
        ) : isAdded ? (
          <span className="text-[10px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm font-medium flex-shrink-0">
            new
          </span>
        ) : (
          <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm font-medium flex-shrink-0">
            modified
          </span>
        )}

        {/* Apply single section button — visible on hover */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onApplySingle();
          }}
          disabled={isApplying}
          className="opacity-0 group-hover/section:opacity-100 transition-opacity text-[10px] font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2 py-0.5 rounded-sm flex-shrink-0 cursor-pointer disabled:opacity-50"
        >
          {isApplying ? '...' : isDeleted ? 'Remove' : isAdded ? 'Add' : 'Apply'}
        </button>
      </div>

      {/* Preview for added sections */}
      {isExpanded && isAdded && (
        <div className="px-3 py-3 bg-emerald-50 border-t border-emerald-100">
          <div
            className="text-xs leading-relaxed text-emerald-900 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: section.suggested.content || '' }}
          />
        </div>
      )}

      {/* Inline word-level diff */}
      {isExpanded && !isDeleted && !isAdded && (
        <div className="px-3 py-3 bg-surface-0 border-t border-surface-100">
          <div className="text-xs leading-relaxed break-words">
            {diffSegments.map((seg, idx) => {
                if (seg.type === 'equal') {
                  return (
                    <span key={idx} className="text-surface-600">
                      {idx > 0 && ' '}
                      {seg.text}
                    </span>
                  );
                }
                if (seg.type === 'remove') {
                  return (
                    <span key={idx}>
                      {idx > 0 && ' '}
                      <span className="bg-red-100 text-red-800 line-through decoration-red-400/60 rounded-[2px] px-0.5">
                        {seg.text}
                      </span>
                    </span>
                  );
                }
                return (
                  <span key={idx}>
                    {idx > 0 && ' '}
                    <span className="bg-emerald-100 text-emerald-800 rounded-[2px] px-0.5">
                      {seg.text}
                    </span>
                  </span>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main panel component ---

export function FullAIEditPanel({
  isLoading,
  suggestion,
  error,
  onClose,
  onSubmit,
  onApplySelected,
  onApplySingle,
  onRejectAll,
  undoSignal,
  initialPrompt,
}: FullAIEditPanelProps) {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('');
  const [showToneOptions, setShowToneOptions] = useState(false);
  const [promptCollapsed, setPromptCollapsed] = useState(false);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [applyingSectionId, setApplyingSectionId] = useState<string | null>(null);
  const [appliedSectionIds, setAppliedSectionIds] = useState<Set<string>>(new Set());
  const [insightTitles, setInsightTitles] = useState<Map<string, string>>(new Map());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const typewriterText = useTypewriterPlaceholder(!prompt && !isLoading && !isPromptFocused);

  // Pre-fill prompt when opened from text selection
  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  // Resolve insight titles for insight_reference sections
  useEffect(() => {
    if (!suggestion) return;
    const allSections: ReportSection[] = [
      ...(suggestion.original_sections || []),
      ...(suggestion.suggested_sections || []),
    ];
    const toResolve = allSections.filter(
      (s) => s.type === 'insight_reference' && s.insight_ref,
    );
    if (toResolve.length === 0) return;

    // Deduplicate by insight_id
    const seen = new Set<string>();
    const unique: { sectionId: string; messageId: string; insightId: string }[] = [];
    for (const s of toResolve) {
      const key = s.insight_ref!.insight_id;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push({
          sectionId: s.id,
          messageId: s.insight_ref!.message_id,
          insightId: s.insight_ref!.insight_id,
        });
      }
    }

    let cancelled = false;
    Promise.all(
      unique.map(async ({ messageId, insightId }) => {
        try {
          const insight = await messageApi.getInsight(messageId, insightId);
          return { insightId, title: insight.title };
        } catch {
          return { insightId, title: null };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const map = new Map<string, string>();
      // Map from section id -> title, using insight_id to match
      for (const s of toResolve) {
        const result = results.find((r) => r.insightId === s.insight_ref!.insight_id);
        if (result?.title) {
          map.set(s.id, result.title);
        }
      }
      setInsightTitles(map);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [suggestion]);

  // Focus textarea on mount
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 150);
  }, []);

  // Auto-accept all changed sections when suggestion arrives; expand prompt on discard
  useEffect(() => {
    if (suggestion?.sections) {
      const changed = suggestion.sections.filter((s) => s.changed || s.deleted);
      setAcceptedIds(new Set(changed.map((s) => s.section_id)));
      setAppliedSectionIds(new Set());
      setAcceptReorder(true);
      setPromptCollapsed(true);
    } else {
      setPromptCollapsed(false);
    }
  }, [suggestion]);

  // When undo happens, restore the section back to the diff list.
  // undoSignal carries the section ID directly — no timing issues.
  useEffect(() => {
    if (undoSignal) {
      const { sectionId } = undoSignal;
      setAppliedSectionIds((prev) => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
      // Re-add to accepted set so it shows as checked
      setAcceptedIds((prev) => new Set(prev).add(sectionId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoSignal]);

  const handleApplySingle = async (sectionId: string) => {
    setApplyingSectionId(sectionId);
    try {
      await onApplySingle(sectionId);
      setAppliedSectionIds((prev) => new Set(prev).add(sectionId));
      // Remove from accepted so the count updates
      setAcceptedIds((prev) => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
    } finally {
      setApplyingSectionId(null);
    }
  };

  // no-op: tone options are now an inline expandable section

  const handleSubmit = async () => {
    if (isLoading) return;
    // Allow submit with just a tone change (no prompt needed)
    const effectivePrompt = prompt.trim() || (tone ? `Rewrite the report in a ${tone} tone` : '');
    if (!effectivePrompt) return;
    await onSubmit(effectivePrompt, tone || 'professional');
  };

  const toggleAccept = (sectionId: string) => {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const changedSections = useMemo(
    () => suggestion?.sections?.filter((s) => (s.changed || s.deleted) && !appliedSectionIds.has(s.section_id)) || [],
    [suggestion, appliedSectionIds],
  );
  const modifiedSections = useMemo(
    () => changedSections.filter((s) => !s.deleted && !s.added),
    [changedSections],
  );
  const addedSections = useMemo(
    () => changedSections.filter((s) => s.added),
    [changedSections],
  );
  const deletedSections = useMemo(
    () => changedSections.filter((s) => s.deleted),
    [changedSections],
  );
  const totalSections = suggestion?.sections?.length || 0;

  // Section outline — shown when sections are reordered OR deleted (shows inline removals + moves)
  const hasReorder = useMemo(
    () => suggestion?.sections?.some((s) => s.reordered) ?? false,
    [suggestion],
  );
  const [acceptReorder, setAcceptReorder] = useState(true);
  const reorderOutline = useMemo(() => {
    if (!hasReorder || !suggestion?.sections) return null;
    const labelFor = (s: ReportSection) => {
      const resolved = insightTitles.get(s.id);
      if (resolved) return `📊 ${resolved}`;
      return extractSectionLabel(s);
    };
    const deletedIdSet = new Set(suggestion.deleted_section_ids || []);
    const origIdSet = new Set((suggestion.original_sections || []).map((s: any) => s.id));
    const origOrder = suggestion.original_sections
      ?.slice()
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((s) => ({ id: s.id, label: labelFor(s), type: s.type })) || [];
    // Exclude new sections from reorder outline — they show as individual diff items
    const newOrder = suggestion.suggested_sections
      ?.slice()
      .filter((s: any) => origIdSet.has(s.id))
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((s) => ({ id: s.id, label: labelFor(s), type: s.type })) || [];
    // Check if surviving sections genuinely reordered (not just shifted by adds/deletions)
    const origSurvivors = origOrder.filter((o) => !deletedIdSet.has(o.id)).map((o) => o.id).join(',');
    const newSurvivors = newOrder.filter((o) => !deletedIdSet.has(o.id)).map((o) => o.id).join(',');
    if (origSurvivors === newSurvivors) return null;

    return { origOrder, newOrder, deletedIdSet };
  }, [hasReorder, suggestion, insightTitles]);


  // Detect if any sections were reordered (shown as compact outline, not per-section diffs)
  const acceptedCount = acceptedIds.size;

  const toneLabel = TONE_OPTIONS.find((t) => t.value === tone)?.label || 'Keep current';

  return (
    <div className="w-[440px] flex-shrink-0 bg-surface-0 border-l border-surface-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <OleumIcon className="w-4 h-4" />
          <span className="text-sm font-semibold text-surface-800">Edit Report with AI</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
        >
          <X className="w-4 h-4 text-surface-500" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {/* Prompt Section — collapses to a single-line summary after results arrive */}
        <div className="flex-shrink-0 border-b border-surface-100">
          {/* Collapsed summary bar */}
          {promptCollapsed && suggestion && (
            <button
              type="button"
              onClick={() => setPromptCollapsed(false)}
              className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-surface-50 transition-colors cursor-pointer"
            >
              <Zap className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
              <span className="text-xs text-surface-600 truncate flex-1">
                {suggestion.prompt_used}
              </span>
              <ChevronRight className="w-3 h-3 text-surface-400 flex-shrink-0" />
            </button>
          )}

          {/* Expandable prompt form — animates via CSS grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateRows: promptCollapsed ? '0fr' : '1fr',
              transition: 'grid-template-rows 250ms ease-out',
            }}
          >
            <div style={{ overflow: 'hidden', minHeight: 0 }}>
              <div className="p-4 space-y-3">
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={() => setIsPromptFocused(true)}
                    onBlur={() => setIsPromptFocused(false)}
                    rows={3}
                    className="w-full px-3 py-3 bg-surface-50 border border-surface-200 rounded-sm text-sm text-surface-800 focus:outline-none focus:ring-2 focus:ring-primary-600/25 focus:border-primary-600 resize-none leading-relaxed"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                  />
                  {!prompt && typewriterText && (
                    <div className="absolute top-0 left-0 px-3 py-3 text-sm text-surface-400 pointer-events-none leading-relaxed">
                      {typewriterText}<span className="inline-block w-[2px] h-[1em] bg-surface-400 ml-[1px] align-middle animate-pulse" />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1" />
                  {suggestion && (
                    <button
                      type="button"
                      onClick={() => setPromptCollapsed(true)}
                      className="text-[11px] text-surface-400 hover:text-surface-600 cursor-pointer"
                    >
                      Collapse
                    </button>
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={(!prompt.trim() && !tone) || isLoading}
                    isLoading={isLoading}
                    leftIcon={!isLoading ? <Zap className="w-4 h-4" /> : undefined}
                  >
                    {isLoading ? 'Generating...' : 'Generate Edit'}
                  </Button>
                </div>

                {/* Tone options — collapsed by default, or expanded when tone is set */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowToneOptions(!showToneOptions)}
                    className="text-[11px] text-surface-400 hover:text-surface-600 cursor-pointer flex items-center gap-1"
                  >
                    {showToneOptions ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {tone ? `Tone: ${toneLabel}` : 'Or just change the tone'}
                  </button>
                  <span className="text-[11px] text-surface-400 ml-auto">{'\u2318'}+Enter to submit</span>
                </div>

                {showToneOptions && (
                  <div className="flex flex-wrap gap-1.5">
                    {TONE_OPTIONS.filter((opt) => opt.value !== '').map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setTone(tone === opt.value ? '' : opt.value)}
                        className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors cursor-pointer ${
                          tone === opt.value
                            ? 'bg-primary-50 text-primary-700 border-primary-300 font-medium'
                            : 'text-surface-600 border-surface-200 hover:bg-surface-50 hover:border-surface-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-start gap-2 flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center py-12 text-center px-4">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-3" />
            <p className="text-sm text-surface-600">
              Reviewing your report and generating suggestions...
            </p>
          </div>
        )}

        {/* Diff results */}
        {suggestion && !isLoading && (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Summary bar */}
            <div className="px-4 py-3 bg-surface-50 border-b border-surface-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-surface-800">
                  {(() => {
                    const parts: string[] = [];
                    if (modifiedSections.length > 0) parts.push(`${modifiedSections.length} modified`);
                    if (addedSections.length > 0) parts.push(`${addedSections.length} added`);
                    if (deletedSections.length > 0) parts.push(`${deletedSections.length} removed`);
                    if (reorderOutline) parts.push('reordered');
                    return parts.length > 0 ? parts.join(', ') : `${changedSections.length} of ${totalSections} sections modified`;
                  })()}
                </span>
              </div>
              {suggestion.explanation && (
                <p className="text-xs text-surface-500 mt-1.5 leading-relaxed">
                  {suggestion.explanation}
                </p>
              )}
            </div>

            {/* Select all / none controls */}
            {changedSections.length > 0 && (
              <div className="px-4 py-2 border-b border-surface-100 flex items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setAcceptedIds(new Set(changedSections.map((s) => s.section_id)));
                    if (reorderOutline) setAcceptReorder(true);
                  }}
                  className="text-[11px] text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => { setAcceptedIds(new Set()); setAcceptReorder(false); }}
                  className="text-[11px] text-surface-500 hover:text-surface-600 font-medium cursor-pointer"
                >
                  Select none
                </button>
                <span className="text-[11px] text-surface-400 ml-auto">
                  {acceptedCount + (acceptReorder && reorderOutline ? 1 : 0)} of {changedSections.length + (reorderOutline ? 1 : 0)} selected
                </span>
              </div>
            )}

            {/* Section diffs */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Reorder outline — single accept/reject block */}
              {reorderOutline && (
                <div className={`mb-4 border rounded-md overflow-hidden ${acceptReorder ? 'border-blue-200 bg-blue-50/50' : 'border-surface-200 bg-surface-50/50 opacity-60'}`}>
                  <div className={`px-3 py-2 border-b flex items-center gap-2 ${acceptReorder ? 'bg-blue-100/60 border-blue-200' : 'bg-surface-100/60 border-surface-200'}`}>
                    <button
                      type="button"
                      onClick={() => setAcceptReorder(!acceptReorder)}
                      className={`w-5 h-5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                        acceptReorder
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-surface-300 hover:border-surface-400'
                      }`}
                    >
                      {acceptReorder && <Check className="w-3 h-3" />}
                    </button>
                    <span className={`text-xs font-medium ${acceptReorder ? 'text-blue-700' : 'text-surface-500'}`}>Section order changed</span>
                  </div>
                  <div className="px-3 py-2.5 space-y-0.5">
                    {(() => {
                      // Build original labels using full original list (for "was #X" display)
                      const origLabels = new Map<string, string>();
                      let origContentIdx = 0;
                      reorderOutline.origOrder.forEach((o, idx) => {
                        const isTitle = idx === 0 && o.type === 'heading';
                        if (!isTitle) origContentIdx++;
                        origLabels.set(o.id, isTitle ? 'Title' : String(origContentIdx));
                      });

                      // Build survivor order for detecting genuine moves (excludes deletions)
                      const origSurvivors = reorderOutline.origOrder.filter(
                        (o) => !reorderOutline.deletedIdSet.has(o.id)
                      );

                      let contentIdx = 0;
                      return reorderOutline.newOrder.map((item, i) => {
                        const isTitleSection = i === 0 && item.type === 'heading';
                        if (!isTitleSection) contentIdx++;
                        const origSurvivorIdx = origSurvivors.findIndex((o) => o.id === item.id);
                        const moved = origSurvivorIdx !== i;
                        return (
                          <div
                            key={item.id}
                            className={`flex items-center gap-2 text-[11px] py-0.5 ${
                              moved ? 'text-blue-700 font-medium' : 'text-surface-500'
                            }`}
                          >
                            <span className="w-6 text-right text-surface-400 flex-shrink-0">
                              {isTitleSection ? 'Title' : `${contentIdx}.`}
                            </span>
                            <span className="truncate flex-1">{item.label}</span>
                            {moved && (
                              <span className="text-[10px] text-blue-500 flex-shrink-0">
                                was #{origLabels.get(item.id) ?? origSurvivorIdx + 1}
                              </span>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
              {(() => {
                const cardSections = changedSections;
                if (cardSections.length === 0 && !reorderOutline) {
                  return (
                    <p className="text-sm text-surface-500 text-center py-8">
                      No changes were suggested for this prompt.
                    </p>
                  );
                }
                return cardSections.map((section) => {
                  const resolved = insightTitles.get(section.original.id);
                  return (
                    <SectionDiff
                      key={section.section_id}
                      section={section}
                      isAccepted={acceptedIds.has(section.section_id)}
                      onToggleAccept={() => toggleAccept(section.section_id)}
                      onApplySingle={() => handleApplySingle(section.section_id)}
                      isApplying={applyingSectionId === section.section_id}
                      resolvedLabel={resolved ? `📊 ${resolved}` : undefined}
                    />
                  );
                });
              })()}
            </div>

            {/* Footer actions */}
            <div className="p-4 border-t border-surface-200 flex items-center gap-2 flex-shrink-0">
              {(acceptedCount > 0 || (acceptReorder && reorderOutline)) ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRejectAll}
                    leftIcon={<RotateCcw className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Discard
                  </Button>
                  <Button
                    onClick={() => onApplySelected(acceptedIds, acceptReorder && !!reorderOutline)}
                    leftIcon={<Check className="w-4 h-4" />}
                    className="flex-1"
                  >
                    {(() => {
                      const parts: string[] = [];
                      if (acceptedCount > 0) parts.push(`${acceptedCount} edit${acceptedCount !== 1 ? 's' : ''}`);
                      if (acceptReorder && reorderOutline) {
                        parts.push('reorder');
                      }
                      return `Apply ${parts.join(' + ')}`;
                    })()}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onClose}
                  variant="ghost"
                  className="flex-1"
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
